import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { AnimatedDiv } from "@/components/motion";

const productCategories = [
  {
    title: "Unstitched Dress Materials",
    desc: "Premium unstitched suit pieces in diverse fabric compositions, ideal for retail and custom tailoring markets.",
    image: "https://images.unsplash.com/photo-1630961680768-998a170045fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmYWJyaWMlMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cCUyMHNpbGslMjBjb3R0b258ZW58MHx8fHwxNzczNzQyOTk3fDA&ixlib=rb-4.1.0&q=85",
    applications: ["Retail", "Export", "Customization"],
    composition: "Cotton, Silk, Viscose, Blends",
  },
  {
    title: "Printed Fabrics",
    desc: "Digitally printed and screen-printed fabrics with vibrant, durable patterns for contemporary fashion.",
    image: "https://images.unsplash.com/photo-1659767151200-f2543af40c44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx0ZXh0aWxlJTIwZmFicmljJTIwZW1icm9pZGVyeSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzczNzQzMDc3fDA&ixlib=rb-4.1.0&q=85",
    applications: ["Fashion Retail", "Export", "Bulk Orders"],
    composition: "Cotton, Polyester, Georgette, Chiffon",
  },
  {
    title: "Embroidered Textiles",
    desc: "Intricately embroidered fabrics combining traditional artistry with modern machine precision.",
    image: "https://images.unsplash.com/photo-1607300110843-b3994a493d98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmYWJyaWMlMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cCUyMHNpbGslMjBjb3R0b258ZW58MHx8fHwxNzczNzQyOTk3fDA&ixlib=rb-4.1.0&q=85",
    applications: ["Premium Retail", "Export", "Designer Collections"],
    composition: "Silk, Organza, Net, Velvet",
  },
  {
    title: "Custom Developments",
    desc: "Bespoke fabric development tailored to your brand's unique design requirements and specifications.",
    image: "https://images.unsplash.com/photo-1718117075248-3d3c3cd65264?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwcm9sbHMlMjBmYWJyaWMlMjB0ZXh0dXJlJTIwd2FyZWhvdXNlfGVufDB8fHwxNzczNzQzMDA1fDA&ixlib=rb-4.1.0&q=85",
    applications: ["Brand Development", "Private Label", "OEM"],
    composition: "As per specification",
  },
  {
    title: "Ready Garments",
    desc: "Finished garments ready for market, from casual wear to premium occasion wear.",
    image: "https://images.unsplash.com/photo-1660980041852-230420b8f99f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0ZXh0aWxlJTIwZmFjdG9yeSUyMG1hY2hpbmVyeSUyMGludGVyaW9yJTIwaGlnaCUyMHRlY2h8ZW58MHx8fHwxNzczNzQyOTk4fDA&ixlib=rb-4.1.0&q=85",
    applications: ["Retail", "Export", "E-commerce"],
    composition: "Various",
  },
];

const ProductsHero = () => (
  <section className="py-24 lg:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="section-divider mb-8" />
      <h1 className="text-5xl md:text-7xl font-serif tracking-tight text-foreground mb-6">Our Products</h1>
      <p className="text-base md:text-lg font-sans text-muted-foreground max-w-xl leading-relaxed">
        A comprehensive portfolio designed to meet evolving market demands.
      </p>
    </div>
  </section>
);

function ProductCategorySection({ product, index }) {
  const reversed = index % 2 !== 0;
  return (
    <section className={`py-20 lg:py-28 ${index % 2 === 0 ? 'bg-background' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <AnimatedDiv className={reversed ? 'lg:order-2' : ''}>
            <div className="aspect-[4/3] overflow-hidden product-card-hover">
              <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
            </div>
          </AnimatedDiv>
          <AnimatedDiv className={reversed ? 'lg:order-1' : ''}>
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {String(index + 1).padStart(2, '0')}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground mb-4">{product.title}</h2>
            <p className="text-base font-sans text-muted-foreground leading-relaxed mb-6">{product.desc}</p>
            <div className="space-y-3 mb-8">
              <div>
                <p className="text-xs font-sans uppercase tracking-[0.15em] text-foreground/60 mb-1">Composition</p>
                <p className="text-sm font-sans text-foreground">{product.composition}</p>
              </div>
              <div>
                <p className="text-xs font-sans uppercase tracking-[0.15em] text-foreground/60 mb-1">Applications</p>
                <div className="flex flex-wrap gap-2">
                  {product.applications.map((app) => (
                    <span key={app} className="text-xs font-sans px-3 py-1 bg-background border border-border text-foreground/70">
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedDiv>
        </div>
      </div>
    </section>
  );
}

const ProductsCTA = () => (
  <section className="py-24 bg-[#0F172A]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">Interested in our products?</h2>
      <p className="text-base font-sans text-white/60 mb-10">Get in touch to discuss your requirements or request a catalogue.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/contact">
          <Button data-testid="products-catalogue-btn" className="bg-[#C5A059] text-[#0F172A] rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#C5A059]/90">
            Request Catalogue <ArrowRight size={14} className="ml-2" />
          </Button>
        </Link>
        <Link to="/contact">
          <Button data-testid="products-contact-btn" variant="outline" className="border-white text-white rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-white hover:text-[#0F172A] bg-transparent">
            Contact Us
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export default function ProductsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="products-page" className="pt-20 lg:pt-24">
      <SEO
        title="Textile Products | Unstitched Dress Materials, Printed Fabrics & Readymade Garments — KF Prints"
        description="Explore KF Prints' product range: unstitched dress materials, printed & embroidered fabrics, and readymade garments. Bulk manufacturing and export from Surat, India to global markets."
        keywords="unstitched dress materials wholesale, printed fabrics exporter, readymade garments manufacturer India, textile products Surat, bulk fabric supplier"
        path="/products"
      />
      <ProductsHero />
      {productCategories.map((product, index) => (
        <ProductCategorySection key={product.title} product={product} index={index} />
      ))}
      <ProductsCTA />
    </div>
  );
}
